import type { ReactNode } from "react";
import { movieDecadeOptions, movieGenreOptions, movieTypeOptions } from "@/mocks/movies";

type FilterPanelProps = {
  type: string;
  decade: string;
  genre: string;
  onTypeChange: (value: string) => void;
  onDecadeChange: (value: string) => void;
  onGenreChange: (value: string) => void;
  onReset: () => void;
};

const Group = ({
  title,
  icon,
  children,
}: {
  title: string;
  icon: string;
  children: ReactNode;
}) => (
  <div className="border-b border-background-200 py-5 first:pt-0 last:border-b-0 last:pb-0">
    <p className="mb-3 flex items-center gap-1.5 font-heading text-xs font-semibold text-foreground-900">
      <i className={`${icon} text-accent-600`} />
      {title}
    </p>
    {children}
  </div>
);

export default function FilterPanel({
  type,
  decade,
  genre,
  onTypeChange,
  onDecadeChange,
  onGenreChange,
  onReset,
}: FilterPanelProps) {
  const active = type !== "全部" || decade !== "全部" || genre !== "全部";

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="mb-4 flex items-center justify-between border-b border-background-200 pb-4">
        <p className="flex items-center gap-1.5 font-heading text-sm font-semibold text-foreground-950">
          <i className="ri-filter-3-line text-accent-600" />
          筛选条件
        </p>
        <button
          type="button"
          onClick={onReset}
          disabled={!active}
          className="cursor-pointer whitespace-nowrap text-[11px] text-foreground-500 transition-colors hover:text-accent-600 disabled:cursor-not-allowed disabled:opacity-40"
        >
          重置
        </button>
      </div>

      <Group title="内容类型" icon="ri-movie-line">
        <div className="flex flex-wrap gap-1.5">
          {movieTypeOptions.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => onTypeChange(item)}
              className={`cursor-pointer whitespace-nowrap rounded-md border px-2.5 py-1.5 text-xs transition-colors ${
                type === item
                  ? "border-primary-500 bg-primary-500 text-background-50"
                  : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-foreground-900"
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </Group>

      <Group title="上映年代" icon="ri-calendar-2-line">
        <div className="flex flex-col gap-1">
          {movieDecadeOptions.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => onDecadeChange(item)}
              className={`flex cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-xs transition-colors ${
                decade === item
                  ? "bg-accent-50 font-medium text-accent-700"
                  : "text-foreground-600 hover:bg-background-100"
              }`}
            >
              <span className="flex items-center gap-2">
                <span
                  className={`flex h-3 w-3 items-center justify-center rounded-full border ${
                    decade === item ? "border-accent-500" : "border-background-400"
                  }`}
                >
                  {decade === item && <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />}
                </span>
                {item}
              </span>
              {decade === item && <i className="ri-check-line text-accent-600" />}
            </button>
          ))}
        </div>
      </Group>

      <Group title="题材标签" icon="ri-price-tag-3-line">
        <div className="flex flex-wrap gap-1.5">
          {movieGenreOptions.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => onGenreChange(item)}
              className={`cursor-pointer whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                genre === item
                  ? "border-accent-500 bg-accent-500 text-background-50"
                  : "border-background-300 bg-background-50 text-foreground-600 hover:border-accent-300 hover:text-accent-700"
              }`}
            >
              {item}
            </button>
          ))}
        </div>
      </Group>

      <p className="mt-5 rounded-md bg-background-100 p-3 text-[11px] leading-relaxed text-foreground-500">
        <i className="ri-information-line mr-1 text-accent-500" />
        筛选为前端演示逻辑，数据来自站内演示影片库，可按条件自由组合。
      </p>
    </div>
  );
}